<template>
  <section class="showcase">
    <BlackNovemberBanner
      v-if="campaignActive"
      :countdown="countdown"
      :rules-url="rulesUrl"
      @contact="$emit('contact-sales')" />

    <div class="sc-head">
      <div class="sc-head-text">
        <h2 class="sc-title">{{ title }}</h2>
        <p class="sc-subtitle">Valores por usuário, por mês, no plano mensal.</p>
      </div>
      <button
        v-if="hasIa4comPlans"
        type="button"
        role="switch"
        :aria-checked="showIa4com ? 'true' : 'false'"
        class="sc-switch"
        @click="showIa4com = !showIa4com">
        <span class="sc-toggle" :class="{ 'sc-toggle--on': showIa4com }"><span class="sc-knob"></span></span>
        <span class="sc-switch-label">Ver preços com IA4COM</span>
      </button>
    </div>

    <div class="sc-cards">
      <article
        v-for="plan in visiblePlans"
        :key="plan.code"
        class="sc-card"
        :class="{ 'sc-card--featured': isFeatured(plan) }">
        <span v-if="isFeatured(plan)" class="sc-tag">BLACK NOVEMBER</span>

        <div class="sc-card-head">
          <h3 class="sc-plan-name">{{ plan.name }}</h3>
          <p v-if="plan.recommendedFor" class="sc-plan-for">{{ plan.recommendedFor }}</p>
        </div>

        <div class="sc-price">
          <p class="sc-price-from">
            <!-- Plano grátis leva "R$ 0,00" riscado em cima do "Grátis" (pedido do usuário, 26/10) -->
            <s v-if="plan.monthlyFee === 0">{{ money(0) }}</s>
            <s v-else-if="originalPriceOf(plan)">{{ money(originalPriceOf(plan)) }}</s>
          </p>
          <p class="sc-price-value">{{ plan.monthlyFee === 0 ? 'Grátis' : money(plan.monthlyFee) }}</p>
          <p class="sc-price-unit">{{ plan.monthlyFee > 0 ? 'por usuário/mês' : 'sem custo' }}</p>
        </div>

        <div class="sc-divider"></div>

        <ul class="sc-benefits">
          <li v-for="benefit in benefitsOf(plan)" :key="benefit.key">
            <img :src="benefit.included ? icons.check : icons.minus" alt="" width="18" height="18">
            <span>{{ benefit.label }}</span>
          </li>
          <li v-if="plan.hasIa4com">
            <img :src="icons.stars" alt="" width="18" height="18">
            <button type="button" class="sc-ia4com-link" @click="ia4comModalOpen = true">IA4COM</button>
            <span class="sc-included">incluso</span>
          </li>
        </ul>

        <template v-if="isFeatured(plan) && discountOf(plan)">
          <div class="sc-divider"></div>
          <div class="sc-campaign-box">
            <p class="sc-campaign-title">BLACK NOVEMBER · ATÉ 29/11</p>
            <p class="sc-campaign-text">{{ plan.name }} com {{ discountOf(plan) }}% off <strong>nos 2 primeiros meses</strong></p>
          </div>
        </template>

        <button v-if="assignable" type="button" class="sc-assign" @click="$emit('assign', plan)">Atribuir a usuários</button>
      </article>
    </div>

    <div v-if="comparison.rows.length > 0" class="sc-compare">
      <button
        type="button"
        class="sc-compare-toggle"
        :aria-expanded="comparisonOpen ? 'true' : 'false'"
        @click="comparisonOpen = !comparisonOpen">
        <img
          :src="icons.chevron"
          alt=""
          width="20"
          height="20"
          class="sc-chevron"
          :class="{ 'sc-chevron--closed': !comparisonOpen }">
        Comparar todos os recursos
      </button>
      <div v-if="comparisonOpen" class="sc-table-wrap">
        <table class="sc-table">
          <thead>
            <tr>
              <th class="sc-th-feature">RECURSO</th>
              <th
                v-for="(plan, index) in comparison.columns"
                :key="plan.code"
                :class="{ 'sc-th--top': index === comparison.columns.length - 1 }">
                {{ String(plan.name).toUpperCase() }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in comparison.rows" :key="row.label">
              <td class="sc-td-feature">{{ row.label }}</td>
              <td v-for="(value, index) in row.values" :key="index">
                <img v-if="value === true" :src="icons.check" alt="Incluso" width="18" height="18">
                <img v-else-if="value === false" :src="icons.minus" alt="Não incluso" width="18" height="18">
                <template v-else>{{ value }}</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Ia4comInfoModal
      :visible="ia4comModalOpen"
      :info-url="ia4comInfoUrl"
      @close="ia4comModalOpen = false" />
  </section>
</template>

<script>
import { formatCurrency } from '../../utils/currency'
import { getPlanOriginalPrice } from '../../utils/plan-original-price'
import { BLACK_NOVEMBER_RULES_URL, IA4COM_INFO_URL } from '@/utils/campaign-links'
import {
  CAMPAIGN_BANNER_ENDS_AT,
  isCampaignBannerActive,
  campaignCountdown,
  discountPercent,
  showcasePlans,
  cardBenefits,
  comparisonTable
} from './plan-showcase'
import BlackNovemberBanner from './BlackNovemberBanner.vue'
import Ia4comInfoModal from './Ia4comInfoModal.vue'
import checkIcon from '@/assets/images/plans/check.svg'
import minusIcon from '@/assets/images/plans/minus.svg'
import starsIcon from '@/assets/images/plans/stars.svg'
import chevronIcon from '@/assets/images/plans/chevron-up.svg'

const CLOCK_TICK_MS = 1000

// Cards de preço do topo da tela de planos (revamp do Figma, TEL-2465). Só mostram:
// a contratação continua na lista de usuários, e "Atribuir a usuários" leva até ela.
export default {
  name: 'PlanShowcase',
  components: {
    BlackNovemberBanner,
    Ia4comInfoModal
  },
  props: {
    // Saída de `mapOffersToPricingList` do catálogo de contratação (`purpose: 'contract'`)
    plans: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: '1. Compare os planos'
    },
    // `false` na tela só de consulta (`/pricing`): sem o botão "Atribuir a usuários".
    assignable: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      now: Date.now(),
      clockTimer: null,
      showIa4com: true,
      ia4comModalOpen: false,
      // Tabela comparativa começa fechada, como no Figma (2054:2191)
      comparisonOpen: false,
      rulesUrl: BLACK_NOVEMBER_RULES_URL,
      ia4comInfoUrl: IA4COM_INFO_URL,
      icons: { check: checkIcon, minus: minusIcon, stars: starsIcon, chevron: chevronIcon }
    }
  },
  computed: {
    hasIa4comPlans () {
      return this.plans.some(plan => plan.hasIa4com === true)
    },
    // Tabela sem gêmeos IA4COM não tem o que alternar: mostra os planos que houver.
    visiblePlans () {
      return showcasePlans(this.plans, this.hasIa4comPlans && this.showIa4com)
    },
    // Banner, selo e quadro só para quem tem a campanha no catálogo, e até 29/11.
    campaignActive () {
      return isCampaignBannerActive(this.plans, this.now)
    },
    countdown () {
      return campaignCountdown(this.now)
    },
    comparison () {
      return comparisonTable(this.plans)
    }
  },
  mounted () {
    if (Date.now() < CAMPAIGN_BANNER_ENDS_AT) {
      this.clockTimer = setInterval(this.tick, CLOCK_TICK_MS)
    }
  },
  beforeDestroy () {
    this.stopClock()
  },
  methods: {
    tick () {
      this.now = Date.now()
      if (this.now >= CAMPAIGN_BANNER_ENDS_AT) this.stopClock()
    },
    stopClock () {
      if (this.clockTimer !== null) {
        clearInterval(this.clockTimer)
        this.clockTimer = null
      }
    },
    money (value) {
      return formatCurrency(value)
    },
    originalPriceOf (plan) {
      return getPlanOriginalPrice(plan)
    },
    discountOf (plan) {
      return discountPercent(plan)
    },
    benefitsOf (plan) {
      return cardBenefits(plan)
    },
    isFeatured (plan) {
      return this.campaignActive && plan.isCampaign === true && plan.highlight === true
    }
  }
}
</script>

<style lang="scss" scoped>
$text-primary: #0f172b;
$text-secondary: #45556c;
$text-tertiary: #62748e;
$border: #e2e8f0;
$brand: #1488d6;

.showcase {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;

  p, h2, h3 { margin: 0; }
}

.sc-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
.sc-head-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sc-title { color: $text-primary; font-size: 20px; line-height: 28px; font-weight: 600; letter-spacing: -0.3px; }
.sc-subtitle { color: $text-secondary; font-size: 16px; line-height: 24px; }

.sc-switch {
  display: inline-flex; align-items: center; gap: 12px;
  min-height: 48px;
  padding: 6px 16px 6px 8px;
  border: 1px solid $border;
  border-radius: 9999px;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
}
.sc-toggle {
  display: flex; align-items: center;
  width: 44px; height: 24px;
  padding: 2px;
  border-radius: 9999px;
  background: $border;
  transition: background-color .15s ease;
  &--on { background: $brand; justify-content: flex-end; }
}
.sc-knob {
  width: 20px; height: 20px;
  border-radius: 9999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
}
.sc-switch-label { color: $text-primary; font-size: 14px; line-height: 20px; font-weight: 700; white-space: nowrap; }

.sc-cards { display: flex; align-items: stretch; gap: 16px; }
.sc-card {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex; flex-direction: column; gap: 16px;
  padding: 24px;
  border: 1px solid $border;
  border-radius: 16px;
  background: #fff;
  &--featured { border-color: $text-primary; }
}
.sc-tag {
  position: absolute;
  top: -11px; left: 50%;
  transform: translateX(-50%);
  padding: 2px 8px;
  border-radius: 9999px;
  background: #0b0b0f;
  color: #ffd23f;
  font-size: 12px; line-height: 16px; font-weight: 700;
  white-space: nowrap;
}
.sc-card-head { display: flex; flex-direction: column; gap: 2px; }
.sc-plan-name { color: $text-primary; font-size: 20px; line-height: 28px; font-weight: 600; letter-spacing: -0.3px; }
.sc-plan-for { color: #64748b; font-size: 14px; line-height: 20px; }

.sc-price { display: flex; flex-direction: column; gap: 2px; }
.sc-price-from { min-height: 20px; color: $text-tertiary; font-size: 14px; line-height: 20px; }
.sc-price-value { color: #0a2740; font-size: 30px; line-height: 36px; font-weight: 700; letter-spacing: -0.75px; white-space: nowrap; }
.sc-price-unit { min-height: 20px; color: $text-tertiary; font-size: 14px; line-height: 20px; }

.sc-divider { height: 1px; background: $border; flex-shrink: 0; }

.sc-benefits {
  display: flex; flex-direction: column; gap: 10px;
  margin: 0; padding: 0; list-style: none;
  li { display: flex; align-items: center; gap: 10px; color: $text-secondary; font-size: 14px; line-height: 20px; }
  img { display: block; flex-shrink: 0; }
}
.sc-ia4com-link {
  padding: 0; border: none; background: none;
  color: $brand;
  font-family: inherit; font-size: 14px; line-height: 20px; font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
}
.sc-included {
  padding: 1px 8px;
  border-radius: 9999px;
  background: #eff6ff;
  color: #0069a8;
  font-size: 12px; line-height: 16px; font-weight: 700;
}

.sc-campaign-box {
  display: flex; flex-direction: column; gap: 4px;
  padding: 14px;
  border-radius: 12px;
  background: #0b0b0f;
}
.sc-campaign-title { color: #ffd23f; font-size: 12px; line-height: 16px; font-weight: 700; }
.sc-campaign-text { color: #fff; font-size: 12px; line-height: 16px; strong { font-weight: 700; } }

// Botão sempre no pé do card, alinhado entre os três.
.sc-assign {
  margin-top: auto;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid $border;
  border-radius: 8px;
  background: #fff;
  color: $text-primary;
  font-family: inherit; font-size: 14px; line-height: 20px; font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  &:hover { background: #f8fafc; }
}

.sc-compare { display: flex; flex-direction: column; align-items: flex-start; gap: 16px; }
.sc-compare-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0; border: none; background: none;
  color: $brand;
  font-family: inherit; font-size: 14px; line-height: 20px; font-weight: 600;
  cursor: pointer;
}
.sc-chevron { display: block; flex-shrink: 0; transition: transform .15s ease; }
// O ícone do Figma é o chevron para cima (aberta); fechada, ele aponta para baixo.
.sc-chevron--closed { transform: rotate(180deg); }

.sc-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid $border;
  border-radius: 8px;
  background: #fff;
}
.sc-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  table-layout: fixed;
  th, td {
    padding: 14px 16px;
    border-bottom: 1px solid $border;
    color: $text-secondary;
    font-size: 14px; line-height: 20px;
    text-align: center;
    vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: none; }
  th { padding: 12px 16px; font-size: 12px; line-height: 16px; font-weight: 700; }
  img { display: inline-block; vertical-align: middle; }
  .sc-th-feature, .sc-td-feature { text-align: left; }
  // Nome do recurso numa linha só em telas menores ("Preço por usuário/mês (mensal)").
  .sc-th-feature { width: 30%; }
  .sc-td-feature { font-weight: 500; }
  // Coluna do plano mais completo em destaque, como o "ILIMITADO" do Figma.
  .sc-th--top { color: $brand; }
}

@media (max-width: 860px) {
  .sc-cards { flex-direction: column; }
  .sc-card--featured { margin-top: 8px; }
}
</style>



// WEBPACK FOOTER //
// src/components/Subscription/PlanShowcase.vue